import { useFormikContext } from 'formik';
import { useContext } from 'react';
import { PAYMENT_METHOD_CATEGORY } from 'Components/widgets/payment-info/PaymentInfo.const';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useAppContext } from 'Context/ReactAppContext';
import { useB2BPaymentForm } from 'Components/widgets/payment-info/b2b-payment-info/useB2BPaymentForm';
type Props = {
  isBuyNowPayLaterPlan: boolean;
  paymentProviders: ReadonlyArray<PaymentProvider>;
  paymentPlans: Array<any>;
  hasPaymentProvidersMatchingBlock: boolean;
  showPaymentMethodWithConditions: boolean;
  selectedPaymentProvider: PaymentProvider | null | undefined;
  hasNoPaymentMethods: boolean;
  showPaymentMethodWithConditions?: boolean;
  healthRegulations: LegalInformation;
  isHealthRegulationsChecked: boolean;
  isHostedFields: boolean;
};
export type CommonPaymentFormDisplays = {
  displayFreeDeposit: boolean;
  displayDonation: boolean;
  displayHealthRegulations: boolean;
  displayTermsAndConditions: boolean;
  displayBuyNowPayLater: boolean;
  displayBillingAddressForm: boolean;
  displayPaymentProviderForm: boolean;
  displayPaymentMethodsWithConditions: boolean;
  displayPaymentProvidersForm: boolean;
  displayPaymentMethods: boolean;
  paymentProviders?: ReadonlyArray<PaymentProvider>;
};
export type PaymentFormDisplays = CommonPaymentFormDisplays & {
  displayB2BPaymentPlan?: boolean;
  displayPaymentPlan?: boolean;
  displayB2BPaymentProvidersForm?: boolean;
  displayNewPaymentProvidersImplementation?: boolean;
};
export const usePaymentFormDisplay = ({
  isBuyNowPayLaterPlan,
  paymentProviders,
  paymentPlans,
  hasPaymentProvidersMatchingBlock,
  selectedPaymentProvider,
  showPaymentMethodWithConditions,
  hasNoPaymentMethods,
  healthRegulations,
  isHealthRegulationsChecked,
  isHostedFields,
}: Props) => {
  const formik = useFormikContext();
  const appContext = useAppContext();
  const { donationAmount, isFreeDeposit } = useContext(PaymentInfoContext);
  // Check donation CGU directly from values (not Formik errors) to avoid the lazy-validation
  // timing issue where the error is only set on the next setFieldValue call after the
  // donation CGU field mounts, causing the card to disappear mid-typing.
  const hasDonation = !isBuyNowPayLaterPlan && !!(donationAmount && donationAmount > 0);
  const isTermsAndConditionsChecked =
    !formik.errors?.termsAndConditions?.cgv &&
    (!hasDonation || !!formik.values?.termsAndConditions?.donation);
  const selectedPaymentMethod = selectedPaymentProvider?.paymentMethods?.find(
    ({ id }) => id === formik.values.paymentMethodId,
  );
  const shouldShowPaymentProviderOfSpecificCard =
    isHostedFields &&
    isTermsAndConditionsChecked &&
    formik.values.paymentMethodId &&
    formik.values.paymentConditionId &&
    isHealthRegulationsChecked &&
    selectedPaymentMethod &&
    (selectedPaymentMethod.category === PAYMENT_METHOD_CATEGORY.CREDIT_CARD ||
      selectedPaymentMethod.category === PAYMENT_METHOD_CATEGORY.CARD);
  const shouldShowPaymentProviderWithNoPaymentMethods =
    isHostedFields && isTermsAndConditionsChecked && isHealthRegulationsChecked;
  const displayDonation =
    !isBuyNowPayLaterPlan &&
    !!(donationAmount !== undefined) &&
    isFeatureFlipped(appContext)('booking.displayDonation');
  const displayPaymentProviderForm = !!(hasNoPaymentMethods
    ? shouldShowPaymentProviderWithNoPaymentMethods
    : shouldShowPaymentProviderOfSpecificCard);
  const displayPaymentProvidersForm = !!(
    (paymentProviders.length > 1 ||
      (paymentProviders.length === 1 && selectedPaymentProvider?.paymentMethods?.length)) &&
    !isBuyNowPayLaterPlan
  );
  const displayPaymentMethods = !!(
    selectedPaymentProvider?.paymentMethods?.length && !hasPaymentProvidersMatchingBlock
  );
  return useB2BPaymentForm({
    displayFreeDeposit: !!isFreeDeposit,
    displayDonation,
    displayHealthRegulations: !!healthRegulations,
    displayTermsAndConditions: true,
    displayPaymentPlan: true,
    displayPaymentMethodsWithConditions: !!showPaymentMethodWithConditions,
    displayBuyNowPayLater: !!paymentPlans.length,
    displayBillingAddressForm:
      !!selectedPaymentProvider?.withBillingAddress &&
      (!isHostedFields || shouldShowPaymentProviderWithNoPaymentMethods),
    displayPaymentProviderForm,
    displayPaymentProvidersForm,
    displayPaymentMethods,
    paymentProviders,
  });
};
